import { snakeCase, startCase } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { Badge, Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { Arrow90degLeft, Arrow90degRight, ChevronRight, InfoCircle } from 'react-bootstrap-icons';
import UnderlineButton from '../UnderlineButton';
import useLocalization from '../../../hooks/useLocalization';
import { isValidObject } from '../../../helpers/global';
import ObjectDetails from '../ObjectDetails';

function formatPath(path) {
  return path
    .map((segment, index, arr) => {
      if (Number.isInteger(segment)) {
        // If the segment is a number, we consider it as an index for user-friendly presentation
        // We add 1 because array indices start from 0, but for non-developers, it's more natural to start from 1
        return (segment + 1).toString();
      } else {
        // If the segment is a string, we format it using lodash's startCase function
        return startCase(segment);
      }
    })
    .join(' > '); // join the segments with ' > ' to form a path
}

const UpdateRow = ({ diff }) => {
  return (
    <>
      {diff.changes.map((change, index) => (
        <h6 key={index} className="mb-1 mid">
          <b className="text-muted">{startCase(change.key)}: </b>
          {!['number', 'boolean'].includes(typeof change?.old) && !change?.old ? 'N/A' : change?.old?.toString()}
          <ChevronRight className="mx-1" size={8} />
          {!['number', 'boolean'].includes(typeof change?.new) && !change?.new ? 'N/A' : change?.new?.toString()}
        </h6>
      ))}
    </>
  );
};

const priorityKeys = [
  'name',
  'description',
  'type',
  'transferValueType',
  'frequency',
  'address',
  'value',
  'amount',
  'inputPrice'
];

const AddOrRemoveRow = ({ diff, title }) => {
  const { translate } = useLocalization();

  const renderPopover = change => {
    // only render string properties of the object
    // filter mongo ids and unique ids
    if (!isValidObject(change.value)) return;
    const valueToRender = Object.keys(change.value)
      .filter(
        key => ['string', 'number', 'boolean'].includes(typeof change.value[key]) && !key.toLowerCase().includes('id')
      )
      .sort((a, b) => priorityKeys.indexOf(a) - priorityKeys.indexOf(b))
      .reduce((acc, key) => {
        acc[key] = change.value[key];
        return acc;
      }, {});

    return (
      <Popover id={`popover-${diff._id}-${change.key}`} style={{ minWidth: 400 }}>
        <Popover.Header as="h3" className="smallFont">
          {title || translate(snakeCase(change.change))}
        </Popover.Header>
        <Popover.Body className="p-0" style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
          <ObjectDetails object={valueToRender} priorityKeys={priorityKeys} />
        </Popover.Body>
      </Popover>
    );
  };

  const getPriorityKey = change => {
    return priorityKeys.find(key => change.value?.[key]);
  };

  const DifferenceRow = ({ change, valueToRender, renderIcon }) => {
    // Only render strings, numbers, or booleans - never objects
    const displayValue = isValidObject(valueToRender) ? '' : valueToRender?.toString() || '';
    return (
      <div className="d-flex hover">
        <b className="text-muted">{change.change.toUpperCase()}</b>
        <span className="px-1">
          {displayValue}
          {renderIcon && <InfoCircle className="mx-1" />}
        </span>
      </div>
    );
  };

  return (
    <div>
      {diff.changes.map((change, index) => {
        const isObj = isValidObject(change.value);
        const valueToRender = isObj ? change.value?.[getPriorityKey(change)] : change.value;
        return (
          <div key={index} className="cursor-pointer d-flex">
            <h6 className="mb-1 smallFont">
              {isObj ? (
                <OverlayTrigger key={index} trigger="click" placement="auto" overlay={renderPopover(change)} rootClose>
                  {DifferenceRow({ valueToRender, change, renderIcon: true })}
                </OverlayTrigger>
              ) : (
                DifferenceRow({ valueToRender, change })
              )}
            </h6>
          </div>
        );
      })}
    </div>
  );
};

const LogItem = ({ user, log, disabled, onUndoRedoClick }) => {
  const { translate } = useLocalization();
  return (
    <div
      className={`w-100 px-2 pb-2 mt-2 border border-secondary-dark rounded bg-${
        log.undone ? 'primary-light bg-opacity-50' : 'light'
      }`}
    >
      <div className="d-flex align-items-center">
        <div className="flex-grow-1">
          <p className="mid px-1 mt-1 mb-0">
            <b>{log.user?.name}</b>
            <span className="px-2">
              {translate(log.isCreate ? 'created_this_user_on' : 'made_following_changes_on')}
            </span>
            <span className="text-muted font-weight-bold">
              {moment(log?.['timestamp']).format('MMMM Do YYYY, h:mm a')}
            </span>
          </p>
        </div>
        {!log.isCreate && (
          <UnderlineButton
            onClick={() => onUndoRedoClick(log.undone ? 'redo' : 'undo')}
            disabled={disabled}
            variant={log.undone ? 'primary' : 'danger'}
            Icon={log.undone ? Arrow90degRight : Arrow90degLeft}
            text={translate(log.undone ? 'redo' : 'undo')}
          />
        )}
      </div>

      {log.reason != null && String(log.reason).trim() !== '' && (
        <p className="small mid px-1 mt-1 mb-2">
          <span className="text-muted fw-bold">{translate('reason')}: </span>
          <span className="text-break">{String(log.reason).trim()}</span>
        </p>
      )}

      {!log.isCreate &&
        log &&
        log.diffs.map((diff, index) => (
          <Card key={index} className="border rounded mt-1">
            <Card.Header className="px-2 py-1 mid text-white bg-table-header d-flex justify-content-between align-items-center">
              <b>{formatPath(diff.pathNames) || user?.name || '-'}</b>
              <Badge bg={diff.change === 'added' ? 'success' : diff.change === 'removed' ? 'danger' : 'primary'}>
                {diff.change === 'update' ? 'UPDATED' : diff.change?.toUpperCase()}
              </Badge>
            </Card.Header>
            <Card.Body className="p-2">
              <div>
                {diff.change === 'update' ? (
                  <UpdateRow diff={diff} />
                ) : (
                  <AddOrRemoveRow diff={diff} title={formatPath(diff.pathNames) || user?.name || '-'} />
                )}
              </div>
            </Card.Body>
          </Card>
        ))}
    </div>
  );
};

export default LogItem;
